import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/theme.css";

// Force light theme by default (clear any previous dark theme)
localStorage.setItem("punto-theme", "light");
document.documentElement.classList.remove("dark");

// Optimize font loading
const fontLink = document.createElement('link');
fontLink.rel = 'preload';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
fontLink.as = 'style';
document.head.appendChild(fontLink);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <App />
);

// ─────────────────────────────────────────────────────────────────────────
// Prerender readiness signal
// ─────────────────────────────────────────────────────────────────────────
// Prerender services (LovableHTML, Puppeteer, vite-plugin-prerender) listen
// for the `app:rendered` event before snapshotting the DOM as static HTML.
//
// We do NOT fire this on first React commit — the SPA shell is empty until
// async data, i18n strings, route-level <UniversalSEO />, and main content
// have actually painted. Firing too early produces empty `<div id="root">`
// snapshots and destroys SEO.
//
// Strategy: poll the DOM until we see real content signals, then fire.
// Hard ceiling at 12s so the crawler never hangs.
// ─────────────────────────────────────────────────────────────────────────
const FIRED_FLAG = "__APP_RENDERED__" as const;
const HARD_TIMEOUT_MS = 12_000;
const POLL_INTERVAL_MS = 120;

const isContentReady = (): boolean => {
  const root = document.getElementById("root");
  if (!root) return false;

  // 1. Root must have actual children (React tree mounted).
  if (root.children.length === 0) return false;

  // 2. There must be a visible <h1> with text (or an <h2> as fallback for
  //    listing pages that intentionally use h1 inside a hidden hero).
  const h1 = root.querySelector("h1");
  const h2 = root.querySelector("h2");
  const hasHeading =
    (h1 && (h1.textContent || "").trim().length > 3) ||
    (h2 && (h2.textContent || "").trim().length > 3);
  if (!hasHeading) return false;

  // 3. There must be a <main> or article-like container with real text.
  const main = root.querySelector("main, article, [role='main']") || root;
  const textLength = (main.textContent || "").replace(/\s+/g, " ").trim().length;
  if (textLength < 200) return false;

  // 4. <title> must not be the generic placeholder.
  const title = (document.title || "").trim();
  if (!title || title === "Punto Hispano" || title.length < 10) return false;

  // 5. <meta name="description"> must be set by UniversalSEO.
  const desc = document
    .querySelector('meta[name="description"]')
    ?.getAttribute("content");
  if (!desc || desc.length < 30) return false;

  return true;
};

const fireAppRendered = (reason: "ready" | "timeout") => {
  const w = window as unknown as Record<string, unknown>;
  if (w[FIRED_FLAG]) return;
  w[FIRED_FLAG] = true;
  w["__APP_RENDERED_REASON__"] = reason;
  document.dispatchEvent(new Event("app:rendered"));
  // Helpful debug attribute crawlers can read instead of waiting for the event.
  document.documentElement.setAttribute("data-app-rendered", reason);
};

if (typeof window !== "undefined") {
  const startedAt = Date.now();
  const tick = () => {
    if ((window as unknown as Record<string, unknown>)[FIRED_FLAG]) return;
    if (isContentReady()) {
      fireAppRendered("ready");
      return;
    }
    if (Date.now() - startedAt > HARD_TIMEOUT_MS) {
      fireAppRendered("timeout");
      return;
    }
    setTimeout(tick, POLL_INTERVAL_MS);
  };
  // Start polling on next idle frame so we don't fight initial paint.
  if ("requestIdleCallback" in window) {
    (window as Window & {
      requestIdleCallback: (cb: () => void) => void;
    }).requestIdleCallback(tick);
  } else {
    setTimeout(tick, 200);
  }
}
